import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './AuthProvider';
import { ApiError } from '@frontend/api/client';
import { ForbiddenState, Skeleton } from '@frontend/components/ui/States';

/**
 * Route guard.
 *
 * Two distinct refusals, which must not be conflated:
 *
 *   NOT SIGNED IN → redirect to the sign-in page, remembering where they were going.
 *   SIGNED IN BUT NOT PERMITTED → render the 403 explanation IN PLACE. Redirecting to
 *   the dashboard would be a lie: the page exists, the user simply cannot open it, and
 *   silently bouncing them makes the app feel broken rather than governed.
 *
 * The permission string is exactly the key `requirePermission` uses on the route, so the
 * client-side hide and the server-side refusal cannot disagree about which permission is
 * at stake.
 */
export function ProtectedRoute({
  children,
  permission,
  anyOf,
}: {
  children: ReactNode;
  permission?: string;
  anyOf?: string[];
}): JSX.Element {
  const { status, hasPermission, hasAny, permissions } = useAuth();
  const location = useLocation();

  if (status === 'LOADING') {
    return (
      <div className="page-container space-y-3" role="status" aria-label="Loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (status === 'ANONYMOUS') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // Permissions still resolving — do not flash a 403 at somebody who does have access.
  if (permissions === null && (permission !== undefined || anyOf !== undefined)) {
    return (
      <div className="page-container space-y-3" role="status" aria-label="Loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  const allowed =
    (permission === undefined || hasPermission(permission)) && (anyOf === undefined || hasAny(anyOf));

  if (!allowed) {
    const required = permission ?? anyOf?.join(' or ') ?? 'unknown';
    return (
      <div className="page-container">
        <ForbiddenState
          error={
            new ApiError({
              status: 403,
              kind: 'FORBIDDEN',
              url: location.pathname,
              requiredPermission: required,
              message:
                `You do not have the "${required}" permission, which is required to open this page. ` +
                'Ask an administrator to add it to one of your roles in Admin → Roles & permissions.',
            })
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}

/** Inline guard for a single control. Renders nothing when the permission is absent. */
export function Can({
  permission,
  anyOf,
  children,
  fallback = null,
}: {
  permission?: string;
  anyOf?: string[];
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const { hasPermission, hasAny } = useAuth();
  const allowed =
    (permission === undefined || hasPermission(permission)) && (anyOf === undefined || hasAny(anyOf));
  return <>{allowed ? children : fallback}</>;
}
